'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Heart, Target, AlertTriangle, BookHeart, Trophy, DollarSign, MessageCircle, Users, BookOpen, ShieldAlert } from 'lucide-react';

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  position?: 'center' | 'sidebar';
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Bem-vindo à sua jornada! 🎉',
    description: 'Estamos aqui para apoiar você em cada passo da recuperação. Vamos conhecer as principais funcionalidades da plataforma.',
    icon: Sparkles,
    iconColor: '#C4A882',
    position: 'center',
  },
  {
    id: 'minha-jornada',
    title: 'Minha Jornada',
    description: 'Acompanhe sua sequência de dias sem apostar, veja seu progresso e celebre cada conquista. Seu ponto de partida diário.',
    icon: Heart,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'missoes',
    title: 'Missões Diárias',
    description: 'Complete pequenas missões todos os dias para fortalecer seus hábitos e manter o foco na recuperação.',
    icon: Target,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'crise',
    title: 'Modo Crise',
    description: 'Sentiu vontade de apostar? Acesse o Modo Crise imediatamente para receber suporte e técnicas de enfrentamento.',
    icon: AlertTriangle,
    iconColor: '#fca5a5',
    position: 'sidebar',
  },
  {
    id: 'diario',
    title: 'Diário Emocional',
    description: 'Registre seus sentimentos e emoções diariamente. Entender seus gatilhos é fundamental para a recuperação.',
    icon: BookHeart,
    iconColor: '#C4A882',
    position: 'sidebar',
  },
  {
    id: 'conquistas',
    title: 'Conquistas',
    description: 'Desbloqueie badges e medalhas conforme você avança. Cada marco é uma vitória que merece ser celebrada!',
    icon: Trophy,
    iconColor: '#C4A882',
    position: 'sidebar',
  },
  {
    id: 'dinheiro',
    title: 'Dinheiro Poupado',
    description: 'Veja quanto dinheiro você economizou ao parar de apostar. Uma motivação poderosa para continuar.',
    icon: DollarSign,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'chat',
    title: 'Chat de Apoio',
    description: 'Converse em tempo real com voluntários treinados. Você não está sozinho — há sempre alguém pronto para ouvir.',
    icon: MessageCircle,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'grupos',
    title: 'Fóruns e Grupos de Apoio',
    description: 'Participe de grupos com pessoas que entendem o que você está passando. Compartilhe, ouça e cresça junto.',
    icon: Users,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'recursos',
    title: 'Recursos Educativos',
    description: 'Artigos, vídeos e materiais sobre dependência de apostas para te ajudar a entender e superar o vício.',
    icon: BookOpen,
    iconColor: '#C4A882',
    position: 'sidebar',
  },
  {
    id: 'protecao',
    title: 'Proteção e Controle',
    description: 'Ferramentas para bloquear sites de apostas, controlar finanças e criar barreiras que protegem sua recuperação.',
    icon: ShieldAlert,
    iconColor: '#7C9E87',
    position: 'sidebar',
  },
  {
    id: 'finish',
    title: 'Você está pronto! 💪',
    description: 'A plataforma está aqui para te apoiar a cada passo. Lembre-se: um dia de cada vez. Você consegue!',
    icon: Sparkles,
    iconColor: '#7C9E87',
    position: 'center',
  },
];

const TOUR_KEY = 'onboarding_tour_completed';

interface OnboardingTourProps {
  userId?: string;
}

export default function OnboardingTour({ userId }: OnboardingTourProps) {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const key = `${TOUR_KEY}_${userId}`;
    const done = localStorage.getItem(key);
    if (!done) {
      // Small delay so the page renders first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [userId]);

  const completeTour = useCallback(() => {
    if (userId) {
      localStorage.setItem(`${TOUR_KEY}_${userId}`, 'true');
    }
    setVisible(false);
  }, [userId]);

  const goNext = useCallback(() => {
    if (animating) return;
    if (currentStep >= tourSteps.length - 1) {
      completeTour();
      return;
    }
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setAnimating(false);
    }, 200);
  }, [animating, currentStep, completeTour]);

  const goPrev = useCallback(() => {
    if (animating || currentStep === 0) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setAnimating(false);
    }, 200);
  }, [animating, currentStep]);

  if (!visible) return null;

  const step = tourSteps[currentStep];
  const StepIcon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === tourSteps.length - 1;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
        onClick={completeTour}
      />

      {/* Tour Card */}
      <div
        className="fixed z-50 inset-0 flex items-center justify-center p-4 pointer-events-none"
      >
        <div
          className={`
            pointer-events-auto w-full max-w-md rounded-2xl shadow-2xl border
            transition-all duration-200
            ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
          `}
          style={{
            backgroundColor: '#1A1A2E',
            borderColor: 'rgba(124,158,135,0.3)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 pt-5 pb-3 border-b"
            style={{ borderColor: 'rgba(124,158,135,0.15)' }}
          >
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: 'rgba(124,158,135,0.15)', color: '#7C9E87' }}
              >
                Tour da Plataforma
              </span>
              <span className="text-xs" style={{ color: 'rgba(248,247,244,0.4)' }}>
                {currentStep + 1} / {tourSteps.length}
              </span>
            </div>
            <button
              onClick={completeTour}
              className="p-1.5 rounded-lg transition-colors hover:opacity-70"
              style={{ color: 'rgba(248,247,244,0.45)' }}
              aria-label="Fechar tour"
            >
              <X size={16} />
            </button>
          </div>

          {/* Progress Bar */}
          <div
            className="h-1 mx-5 mt-3 rounded-full overflow-hidden"
            style={{ backgroundColor: 'rgba(124,158,135,0.15)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, backgroundColor: '#7C9E87' }}
            />
          </div>

          {/* Content */}
          <div className="px-5 py-6">
            {/* Icon */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${step.iconColor}20` }}
            >
              <StepIcon size={28} style={{ color: step.iconColor }} />
            </div>

            {/* Title */}
            <h2
              className="text-xl font-bold mb-2 leading-snug"
              style={{ color: '#F8F7F4' }}
            >
              {step.title}
            </h2>

            {/* Description */}
            <p
              className="text-sm leading-relaxed"
              style={{ color: 'rgba(248,247,244,0.7)' }}
            >
              {step.description}
            </p>
          </div>

          {/* Step Dots */}
          <div className="flex justify-center gap-1.5 pb-4">
            {tourSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (!animating) {
                    setAnimating(true);
                    setTimeout(() => {
                      setCurrentStep(idx);
                      setAnimating(false);
                    }, 200);
                  }
                }}
                className="rounded-full transition-all duration-300"
                style={{
                  width: idx === currentStep ? '20px' : '6px',
                  height: '6px',
                  backgroundColor: idx === currentStep
                    ? '#7C9E87'
                    : idx < currentStep
                    ? 'rgba(124,158,135,0.5)'
                    : 'rgba(248,247,244,0.2)',
                }}
                aria-label={`Ir para passo ${idx + 1}`}
              />
            ))}
          </div>

          {/* Footer Buttons */}
          <div
            className="flex items-center justify-between px-5 pb-5 gap-3"
          >
            <button
              onClick={completeTour}
              className="text-xs transition-colors hover:opacity-70"
              style={{ color: 'rgba(248,247,244,0.35)' }}
            >
              Pular tour
            </button>

            <div className="flex gap-2">
              {!isFirst && (
                <button
                  onClick={goPrev}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'rgba(124,158,135,0.12)',
                    color: '#7C9E87',
                    border: '1px solid rgba(124,158,135,0.25)',
                  }}
                >
                  <ChevronLeft size={15} />
                  Anterior
                </button>
              )}
              <button
                onClick={goNext}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
                style={{
                  backgroundColor: '#7C9E87',
                  color: '#1A1A2E',
                }}
              >
                {isLast ? 'Começar!' : 'Próximo'}
                {!isLast && <ChevronRight size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
